import React, { useState, useEffect } from "react";
import { Modal, message, Upload, Button, Alert } from "antd";
import { getUploadUrlApi } from "../../../services/common.services";
import { convertToFormData } from "../../../helpers/utility";
import { CloudUploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { uploadPatientImagesApi } from "../../../services/patient.services";
import modalStyles from "../../PatientsIP/PatientGallery/galleryCompactModal.module.css";
import {
  getDefaultUploadFileName,
  getUploadBatchSizeError,
  getUploadFileNameError,
  GALLERY_FOLDER_KEYS,
  PATIENT_APP_UPLOAD_NOTE,
} from "../../../helpers/galleryUtils";
import UploadFilePreviewList from "../../PatientsIP/PatientGallery/UploadFilePreviewList";

const { Dragger } = Upload;

const IpUploadModal = ({
  openModal,
  setOpenModal,
  editData,
  refreshIpPage,
  type,
  refresh,
}) => {
  const [uploadedFileName, setuploadedFileName] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const modalTitle =
    type === "consent"
      ? "Upload Consent Form"
      : type === "patient_files"
      ? "Upload Patient Files"
      : "Upload Discharge Summary";

  const showPatientAppNote = type === "patient_files";

  const beforeUpload = (file, fileList) => {
    const file_ext = file.name.split(".").pop().toLowerCase();

    const validType =
      ["png", "jpg", "jpeg", "svg"].includes(file_ext) ||
      file.type === "application/pdf";

    if (!validType) {
      message.error(`${file.name} is not an allowed file type`);
      return Upload.LIST_IGNORE;
    }

    const nameError = getUploadFileNameError(file.name);
    if (nameError) {
      message.error(nameError);
      return Upload.LIST_IGNORE;
    }

    const sizeError = getUploadBatchSizeError(fileList);
    if (sizeError) {
      message.error(sizeError);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const uploadFileFunc = async (info) => {
    if (info.file.status === "done") {
      setUploading(true);
      try {
        const res = await getUploadUrlApi(
          `path_key=${info.file.name}&admission_type=IP&admission_id=${editData?.id}`
        );
        const formData = convertToFormData(res, info);
        await axios({
          method: "POST",
          url: res?.data?.url,
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        });
        setuploadedFileName((prevState) => [
          ...prevState,
          {
            path: res?.data?.fields?.key,
            type: info.file.type,
            file_name: getDefaultUploadFileName(info.file),
            preview_url: URL.createObjectURL(
              info.file.originFileObj || info.file
            ),
          },
        ]);
      } catch (err) {
        console.log(err);
        message.error(err?.response?.data?.message);
      } finally {
        setUploading(false);
      }
    }
  };

  const onSubmit = () => {
    const paths = uploadedFileName.map((file) => ({
      path: file.path,
      type: file.type,
      file_name: file.file_name?.trim() || file.path.split("/").pop(),
    }));

    const folderByType = {
      consent: GALLERY_FOLDER_KEYS.CONSENT_FORM,
      patient_files: GALLERY_FOLDER_KEYS.PATIENT_FILES,
      discharge: GALLERY_FOLDER_KEYS.DISCHARGE_SUMMARY,
    };

    const formData = {
      folder: folderByType[type] || GALLERY_FOLDER_KEYS.DISCHARGE_SUMMARY,
      paths,
      ip_admission: editData?.id,
    };

    if (editData?.id) {
      uploadPatientImagesApi(formData)
        .then((res) => {
          message.success(res?.data?.message || "File uploaded successfully");
          refreshIpPage && refreshIpPage();
          refresh();
          setOpenModal(false);
        })
        .catch((err) => {
          message?.error(err?.response?.data?.message);
        });
    }
  };

  const dummyRequest = ({ onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const handleRemoveFile = (fileToRemove) => {
    if (fileToRemove?.preview_url?.startsWith("blob:")) {
      URL.revokeObjectURL(fileToRemove.preview_url);
    }

    setuploadedFileName((prev) =>
      prev.filter((file) => file.path !== fileToRemove.path)
    );
    message.success("Removed file successfully");
  };

  const handleFileNameChange = (path, value) => {
    setuploadedFileName((prev) =>
      prev.map((file) =>
        file.path === path ? { ...file, file_name: value } : file
      )
    );
  };

  return (
    <Modal
      open={openModal}
      title={modalTitle}
      onCancel={() => setOpenModal(false)}
      width={isMobile ? "min(400px, calc(100vw - 24px))" : 400}
      {...(isMobile ? { centered: false } : { centered: true })}
      wrapClassName={modalStyles.ipUploadModalWrap}
      destroyOnClose
      footer={
        <div className="flex-end" style={{ gap: 12, margin: 0 }}>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button
            type="primary"
            onClick={onSubmit}
            disabled={uploading || uploadedFileName.length === 0}
          >
            OK
          </Button>
        </div>
      }
      styles={{
        body: { maxWidth: "100%", overflowX: "hidden", boxSizing: "border-box" },
        footer: { marginTop: 0 },
      }}
    >
      {showPatientAppNote && (
        <Alert
          type="info"
          showIcon
          message="Visible in Patient App"
          description={PATIENT_APP_UPLOAD_NOTE}
          style={{ marginBottom: 12 }}
        />
      )}
      <Dragger
        className={modalStyles.ipUploadDragger}
        multiple
        accept=".png,.jpg,.jpeg,.svg,application/pdf"
        beforeUpload={beforeUpload}
        onChange={(info) => uploadFileFunc(info)}
        customRequest={dummyRequest}
        showUploadList={false}
        style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
      >
        <p className="ant-upload-drag-icon">
          <CloudUploadOutlined />
        </p>
        <p className="ant-upload-text">
          <span style={{ color: "#406AFF" }}>Click to Upload </span>
        </p>
        <p className="ant-upload-hint">Pdf, jpg, jpeg, png, svg</p>
      </Dragger>
      <UploadFilePreviewList
        uploadFile={uploadedFileName}
        onRemove={handleRemoveFile}
        onFileNameChange={handleFileNameChange}
        isMobile={isMobile}
      />
    </Modal>
  );
};

export default IpUploadModal;
